import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { Plus, Pencil, Printer } from 'lucide-react';
import { differenceInDays, format, parseISO, startOfDay } from 'date-fns';
import HomeProfileForm from './HomeProfileForm';
import {
  buildFamilyChildChoreSuggestionGroups,
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppFieldGroup,
  AppFormSection,
  AppInput,
  AppInlineAlert,
  AppLoadingState,
  AppNativeSelect,
  AppSectionHeader,
  AppText,
  AppTextActionButton,
} from '../../../ui-foundation';
import {
  buildNextSharedHomeRoutineDueDate,
  buildSharedHomeRoutineDefaults,
  formatSharedHomeRoutineReminderSchedule,
  getSharedHomeRoutineMeta,
  SHARED_HOME_BILL_WEEKDAY_OPTIONS,
  SHARED_HOME_ROUTINE_FREQUENCY_OPTIONS,
  SHARED_HOME_ROUTINE_KINDS,
  type SharedHomeBillWeekday,
  type SharedHomeRoutineFrequency,
  type SharedHomeRoutineKind,
} from '../../../ui-foundation';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
  AppPanelButton,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
} from '../../../ui-foundation/adapters/web';
import {
  buildPropertyCheckedDateLabel,
  formatHomePropertyValueOrigin,
  invokeHomePropertyEnrichment,
  type HomePropertyEnrichmentResult,
} from '@/lib/home-property-enrichment';
import FamilyMealPlannerSection from '@/components/family/FamilyMealPlannerSection';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import BillsTab from './BillsTab';

export type TenureType = 'renting-private' | 'renting-social' | 'owner-occupier' | 'living-with-family' | 'other';

export interface HomeProfile {
  id: string;
  user_id: string;
  tenure: TenureType | null;
  landlord_name: string | null;
  landlord_email: string | null;
  landlord_phone: string | null;
  address: string | null;
  monthly_rent: number | null;
  deposit_amount: number | null;
  tenancy_start_date: string | null;
  council_tax_band: string | null;
  council_tax_source: string | null;
  council_tax_source_reference: string | null;
  council_tax_last_checked_at: string | null;
  council_tax_match_status: string | null;
  council_tax_confidence: string | null;
  council_tax_value_origin: string | null;
  mortgage_provider: string | null;
  mortgage_fixed_rate_end: string | null;
  epc_rating: string | null;
  epc_score: number | null;
  epc_expiry_date: string | null;
  epc_source: string | null;
  epc_source_reference: string | null;
  epc_last_checked_at: string | null;
  epc_match_status: string | null;
  epc_confidence: string | null;
  epc_value_origin: string | null;
  ground_rent_amount: number | null;
  service_charge_amount: number | null;
  property_lookup_query: string | null;
  property_uprn: string | null;
  property_address_normalised: string | null;
  property_postcode_normalised: string | null;
  has_lawn: boolean | null;
  has_houseplants: boolean | null;
  has_vehicle: boolean | null;
  vehicle_registration_number: string | null;
  vehicle_make: string | null;
  vehicle_mot_due_date: string | null;
  vehicle_mot_status: string | null;
  vehicle_tax_due_date: string | null;
  vehicle_tax_status: string | null;
  vehicle_last_synced_at: string | null;
  utility_gas_provider: string | null;
  utility_electric_provider: string | null;
  utility_broadband_provider: string | null;
  utility_water_provider: string | null;
  utility_phone_provider: string | null;
  utility_gas_contract_end: string | null;
  utility_electric_contract_end: string | null;
  utility_broadband_contract_end: string | null;
  utility_phone_contract_end: string | null;
  notes: string | null;
}

type HomeRecurringRoutine = {
  id: string;
  routine_kind: SharedHomeRoutineKind;
  title: string;
  status: 'active' | 'paused';
  due_date: string;
  frequency: SharedHomeRoutineFrequency;
  reminder_day_of_week: SharedHomeBillWeekday;
  notifications_enabled: boolean;
  calendar_sync_enabled: boolean;
  last_completed_at: string | null;
  notes: string | null;
};

type HomePrimarySectionKey = 'profile' | 'jobs' | 'meals';

export const TENURE_LABELS: Record<string, string> = {
  'renting-private': '🏠 Renting privately',
  'renting-social': '🏢 Renting social',
  'owner-occupier': '🔑 Owner-occupier',
  'living-with-family': '👨‍👩‍👧 Living with family',
  'other': '📋 Other',
};

const GBP_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function isRenting(tenure: string | null) {
  return tenure === 'renting-private' || tenure === 'renting-social';
}

function isOwner(tenure: string | null) {
  return tenure === 'owner-occupier';
}

function getRemortgagePrompt(fixedRateEnd: string | null) {
  if (!fixedRateEnd) return null;
  const daysUntil = differenceInDays(parseISO(fixedRateEnd), startOfDay(new Date()));
  if (daysUntil > 180) return null;
  if (daysUntil <= 30) {
    return {
      title: daysUntil < 0 ? 'Your fixed rate has already ended' : 'Your remortgage window is urgent now',
      body: daysUntil < 0
        ? 'Review your current rate and switch options now so you are not stuck drifting on a higher deal.'
        : `Your fixed rate ends on ${format(parseISO(fixedRateEnd), 'd MMM yyyy')}. Gather the key paperwork and compare deals before the deadline lands.`,
    };
  }
  if (daysUntil <= 90) {
    return {
      title: 'Start remortgage prep properly',
      body: `Your fixed rate ends on ${format(parseISO(fixedRateEnd), 'd MMM yyyy')}. This is the moment to gather income documents, check lender options, and avoid a rushed rollover.`,
    };
  }
  return {
    title: 'Your remortgage prep window is open',
    body: `Your fixed rate ends on ${format(parseISO(fixedRateEnd), 'd MMM yyyy')}. Start comparing deals now so you have room to act before rates change.`,
  };
}

function notifyHomeRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-2.5 border-b border-border last:border-b-0">
      <AppText fontSize="$sm" color="$colorMuted">{label}</AppText>
      <AppText fontSize="$sm" fontWeight="600" textAlign="right" maxWidth="60%">{value}</AppText>
    </div>
  );
}

function Section({ title, onEdit, children }: { title: string; onEdit: () => void; children: React.ReactNode }) {
  const hasContent = (() => {
    let found = false;
    const arr = Array.isArray(children) ? children : [children];
    arr.forEach((c: any) => { if (c) found = true; });
    return found;
  })();
  if (!hasContent) return null;
  return (
    <AppCard mb="$sm">
      <AppCardHeader>
        <div className="flex items-center justify-between gap-3">
          <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
            {title}
          </AppText>
          <AppButton size="sm" tone="ghost" onPress={onEdit}>
            <Pencil size={12} /> Edit
          </AppButton>
        </div>
      </AppCardHeader>
      <AppCardBody gap={0}>{children}</AppCardBody>
    </AppCard>
  );
}

export default function MyHomeTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, data, addChore, toggleChore } = useApp();
  const [profile, setProfile] = useState<HomeProfile | null>(null);
  const [addressEnrichment, setAddressEnrichment] = useState<HomePropertyEnrichmentResult | null>(null);
  const [addressEnrichmentLoading, setAddressEnrichmentLoading] = useState(false);
  const [routines, setRoutines] = useState<HomeRecurringRoutine[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [savingRoutineKind, setSavingRoutineKind] = useState<SharedHomeRoutineKind | null>(null);
  const [expandedRoutineKinds, setExpandedRoutineKinds] = useState<Partial<Record<SharedHomeRoutineKind, boolean>>>({});
  const [choreOpen, setChoreOpen] = useState(false);
  const [choreTask, setChoreTask] = useState('');
  const [choreAssignee, setChoreAssignee] = useState('');
  const [openSection, setOpenSection] = useState<HomePrimarySectionKey | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('home_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    setProfile(data as HomeProfile | null);
    setLoading(false);
  }, [user]);

  const fetchRoutines = useCallback(async () => {
    if (!user) {
      setRoutines([]);
      return;
    }

    const { data } = await supabase
      .from('home_recurring_routines' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('due_date', { ascending: true });

    setRoutines(
      (((data || []) as any[]) || []).map((row) => {
        const defaults = buildSharedHomeRoutineDefaults(row.routine_kind);

        return {
          ...row,
          frequency: row.frequency || defaults.frequency,
          reminder_day_of_week: row.reminder_day_of_week || defaults.reminderDayOfWeek,
          notifications_enabled: row.notifications_enabled ?? defaults.notificationsEnabled,
          calendar_sync_enabled: row.calendar_sync_enabled ?? defaults.calendarSyncEnabled,
        };
      }) as HomeRecurringRoutine[],
    );
  }, [user]);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);
  useEffect(() => {
    void fetchRoutines();
  }, [fetchRoutines]);
  useEffect(() => {
    let cancelled = false;

    if (!profile?.address?.trim()) {
      setAddressEnrichment(null);
      setAddressEnrichmentLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setAddressEnrichmentLoading(true);

    void (async () => {
      const { data: enrichment, error } = await invokeHomePropertyEnrichment(supabase as any, profile.address);
      if (cancelled) return;
      setAddressEnrichment(error ? null : (enrichment || null));
      setAddressEnrichmentLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [profile?.address]);
  useEffect(() => {
    if (searchParams.get('edit')) {
      setFormOpen(true);
    }
  }, [searchParams]);

  const forcedBillsSection = searchParams.get('section') === 'vehicles'
    ? 'vehicle'
    : searchParams.get('section') === 'bills'
      ? 'bills'
      : null;

  const currentWeekKey = format(new Date(), "yyyy-'W'II");
  const thisWeekChores = data.chores.filter((chore) => chore.weekOf === currentWeekKey);
  const assignees = Array.from(new Set([
    data.profile.firstName.trim() || 'Me',
    data.profile.partnerName.trim(),
    ...data.profile.children.map((child) => child.name.trim()),
  ].filter(Boolean)));
  const childChoreSuggestionGroups = buildFamilyChildChoreSuggestionGroups(
    data.profile.children
      .filter((child) => child.name.trim())
      .map((child) => ({ id: child.id, name: child.name, age: child.age })),
    thisWeekChores,
  );
  const hasChildProfiles = data.profile.children.some((child) => child.name.trim());

  useEffect(() => {
    if (!choreAssignee && assignees[0]) {
      setChoreAssignee(assignees[0]);
    }
  }, [assignees, choreAssignee]);

  const handleSaved = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
    setFormOpen(false);
    fetchProfile();
  };

  const closeEditor = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
    setFormOpen(false);
  };

  const turnOnRoutine = useCallback(async (kind: SharedHomeRoutineKind) => {
    if (!user) return;

    setSavingRoutineKind(kind);
    const defaults = buildSharedHomeRoutineDefaults(kind);

    await supabase.from('home_recurring_routines' as any).upsert(
      {
        user_id: user.id,
        routine_kind: kind,
        title: defaults.title,
        status: 'active',
        due_date: defaults.dueDate,
        frequency: defaults.frequency,
        reminder_day_of_week: defaults.reminderDayOfWeek,
        notifications_enabled: defaults.notificationsEnabled,
        calendar_sync_enabled: defaults.calendarSyncEnabled,
        last_completed_at: null,
      },
      { onConflict: 'user_id,routine_kind' },
    );

    notifyHomeRefresh();
    await fetchRoutines();
    setExpandedRoutineKinds((current) => ({ ...current, [kind]: true }));
    setSavingRoutineKind(null);
  }, [fetchRoutines, user]);

  const completeRoutine = useCallback(async (routine: HomeRecurringRoutine) => {
    setSavingRoutineKind(routine.routine_kind);
    const completedAt = new Date();

    await supabase
      .from('home_recurring_routines' as any)
      .update({
        status: 'active',
        last_completed_at: completedAt.toISOString(),
        due_date: buildNextSharedHomeRoutineDueDate(routine.routine_kind, completedAt, {
          frequency: routine.frequency,
          reminderDayOfWeek: routine.reminder_day_of_week,
        }),
      })
      .eq('id', routine.id);

    notifyHomeRefresh();
    await fetchRoutines();
    setSavingRoutineKind(null);
  }, [fetchRoutines]);

  const setRoutineStatus = useCallback(async (routine: HomeRecurringRoutine, status: 'active' | 'paused') => {
    setSavingRoutineKind(routine.routine_kind);

    await supabase
      .from('home_recurring_routines' as any)
      .update({ status })
      .eq('id', routine.id);

    notifyHomeRefresh();
    await fetchRoutines();
    setSavingRoutineKind(null);
  }, [fetchRoutines]);

  const updateRoutineSettings = useCallback(async (
    routine: HomeRecurringRoutine,
    updates: Partial<Pick<HomeRecurringRoutine, 'frequency' | 'reminder_day_of_week' | 'notifications_enabled' | 'calendar_sync_enabled'>>,
  ) => {
    setSavingRoutineKind(routine.routine_kind);

    const nextFrequency = updates.frequency || routine.frequency;
    const nextReminderDay = updates.reminder_day_of_week || routine.reminder_day_of_week;
    const nextReferenceDate = routine.last_completed_at ? parseISO(routine.last_completed_at) : new Date();

    await supabase
      .from('home_recurring_routines' as any)
      .update({
        ...updates,
        due_date: buildNextSharedHomeRoutineDueDate(routine.routine_kind, nextReferenceDate, {
          frequency: nextFrequency,
          reminderDayOfWeek: nextReminderDay,
        }),
      })
      .eq('id', routine.id);

    notifyHomeRefresh();
    await fetchRoutines();
    setSavingRoutineKind(null);
  }, [fetchRoutines]);

  const toggleRoutineExpanded = useCallback((kind: SharedHomeRoutineKind) => {
    setExpandedRoutineKinds((current) => ({
      ...current,
      [kind]: !current[kind],
    }));
  }, []);

  const handleAddChore = useCallback(() => {
    if (!choreTask.trim() || !choreAssignee.trim()) return;

    addChore({
      id: crypto.randomUUID(),
      task: choreTask.trim(),
      assignedTo: choreAssignee.trim(),
      done: false,
      weekOf: currentWeekKey,
    });
    setChoreTask('');
    setChoreAssignee(assignees[0] || 'Me');
    setChoreOpen(false);
  }, [addChore, assignees, choreAssignee, choreTask, currentWeekKey]);

  const addSuggestedChore = useCallback((assignedTo: string, task: string) => {
    addChore({
      id: crypto.randomUUID(),
      task,
      assignedTo,
      done: false,
      weekOf: currentWeekKey,
    });
  }, [addChore, currentWeekKey]);

  const printChoreChart = useCallback(() => {
    const printWindow = window.open('', 'adulting-home-jobs', 'width=900,height=700');
    if (!printWindow) return;

    const suggestedHtml = childChoreSuggestionGroups.map((group) => (
      `<section class="child-card">
        <h2>${group.child.name} <span>Age ${group.child.age}</span></h2>
        <p class="age-label">${group.ageLabel}</p>
        <ul>
          ${group.suggestions.map((item) => (
            `<li><strong>${item.task}</strong><span>${item.note}</span></li>`
          )).join('')}
        </ul>
      </section>`
    )).join('');

    const currentWeekHtml = thisWeekChores.length > 0
      ? `<section class="current-card">
          <h2>This week's jobs</h2>
          <ul>
            ${thisWeekChores.map((chore) => (
              `<li><strong>${chore.task}</strong><span>${chore.assignedTo}${chore.done ? ' · done' : ''}</span></li>`
            )).join('')}
          </ul>
        </section>`
      : '';

    printWindow.document.write(`
      <html>
        <head>
          <title>Adulting home jobs and routines</title>
          <style>
            body { font-family: Georgia, 'Times New Roman', serif; margin: 32px; color: #1f2937; }
            h1 { font-size: 28px; margin-bottom: 8px; }
            .subtitle { color: #6b7280; margin-bottom: 24px; }
            .child-card, .current-card { border: 1px solid #d6d3d1; border-radius: 16px; padding: 16px 18px; margin-bottom: 16px; }
            h2 { font-size: 18px; margin: 0 0 6px; display: flex; justify-content: space-between; gap: 12px; }
            h2 span { color: #6b7280; font-size: 13px; font-weight: 400; }
            .age-label { margin: 0 0 12px; color: #92400e; font-size: 13px; }
            ul { margin: 0; padding-left: 18px; }
            li { margin-bottom: 10px; }
            li strong { display: block; font-size: 14px; }
            li span { color: #6b7280; font-size: 13px; }
          </style>
        </head>
        <body>
          <h1>Adulting home jobs and routines</h1>
          <p class="subtitle">Use the saved child ages as a starting point for home jobs, then keep the current week visible alongside the recurring routines.</p>
          ${currentWeekHtml}
          ${suggestedHtml || '<p class="subtitle">Add a child profile with an age in My Settings and Adulting will suggest steady household jobs here.</p>'}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [childChoreSuggestionGroups, thisWeekChores]);

  if (loading) {
    return <AppLoadingState title="Loading home details" body="Pulling your saved home profile and utility context." />;
  }

  if (formOpen) {
    return <HomeProfileForm existing={profile} onClose={closeEditor} onSaved={handleSaved} />;
  }

  if (!profile) {
    return (
      <AppEmptyState
        title="No home profile yet"
        body="Add your home details so tenancy, utilities, and the basics stay in one place."
        action={
          <AppButton onPress={() => setFormOpen(true)}>
            <Plus size={16} /> Set up my home
          </AppButton>
        }
      />
    );
  }

  const renting = isRenting(profile.tenure);
  const owner = isOwner(profile.tenure);
  const openEdit = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('edit', 'details');
    setSearchParams(nextParams, { replace: true });
    setFormOpen(true);
  };
  const toggleHomeSection = (section: HomePrimarySectionKey) => {
    setOpenSection((current) => (current === section ? null : section));
  };
  const hasOwnerDetails = Boolean(
    profile.mortgage_provider
    || profile.mortgage_fixed_rate_end
    || profile.epc_rating
    || profile.ground_rent_amount != null
    || profile.service_charge_amount != null
  );
  const utilityProvidersCount = [
    profile.utility_gas_provider,
    profile.utility_electric_provider,
    profile.utility_broadband_provider,
    profile.utility_water_provider,
    profile.utility_phone_provider,
  ].filter(Boolean).length;
  const renewalUtilityProvidersCount = [
    profile.utility_gas_provider,
    profile.utility_electric_provider,
    profile.utility_broadband_provider,
    profile.utility_phone_provider,
  ].filter(Boolean).length;
  const utilityContracts = [
    {
      label: 'Gas',
      provider: profile.utility_gas_provider,
      contractEnd: profile.utility_gas_contract_end,
    },
    {
      label: 'Electric',
      provider: profile.utility_electric_provider,
      contractEnd: profile.utility_electric_contract_end,
    },
    {
      label: 'Broadband',
      provider: profile.utility_broadband_provider,
      contractEnd: profile.utility_broadband_contract_end,
    },
    {
      label: 'Phone',
      provider: profile.utility_phone_provider,
      contractEnd: profile.utility_phone_contract_end,
    },
  ];
  const contractDatesMissing = utilityContracts.filter((item) => item.provider && !item.contractEnd);
  const nextComparisonWindow = utilityContracts
    .filter((item) => item.provider && item.contractEnd)
    .map((item) => ({
      ...item,
      daysUntil: differenceInDays(parseISO(item.contractEnd as string), startOfDay(new Date())),
    }))
    .filter((item) => item.daysUntil >= 0 && item.daysUntil <= 45)
    .sort((a, b) => a.daysUntil - b.daysUntil)[0] ?? null;
  const remortgagePrompt = owner ? getRemortgagePrompt(profile.mortgage_fixed_rate_end) : null;
  const routineCards = SHARED_HOME_ROUTINE_KINDS.map((kind) => {
    const routine = routines.find((item) => item.routine_kind === kind) || null;
    const meta = getSharedHomeRoutineMeta(kind);
    const defaults = buildSharedHomeRoutineDefaults(kind);
    const frequency = routine?.frequency || defaults.frequency;
    const reminderDayOfWeek = routine?.reminder_day_of_week || defaults.reminderDayOfWeek;

    return {
      kind,
      routine,
      defaults,
      frequency,
      reminderDayOfWeek,
      scheduleSummary: formatSharedHomeRoutineReminderSchedule(frequency, reminderDayOfWeek),
      isExpanded: expandedRoutineKinds[kind] ?? false,
      title: meta.title,
      inactiveBody: meta.inactiveBody,
      emptyAction: meta.enableLabel,
    };
  });
  const visibleRoutineCards = routineCards.filter(({ kind, routine }) => {
    if (routine) return true;
    if (kind === 'houseplants') return profile.has_houseplants !== false;
    if (kind === 'lawn') return profile.has_lawn !== false;
    if (kind === 'wash-car' || kind === 'valet-inside-car') return profile.has_vehicle !== false;
    return true;
  });
  const monthlyHomeCost = profile.monthly_rent != null ? `£${profile.monthly_rent.toFixed(2)}` : null;
  const propertyCheckedLabel = buildPropertyCheckedDateLabel(
    profile.epc_last_checked_at || profile.council_tax_last_checked_at,
  );
  const propertySourceLabels = Array.from(new Set([
    formatHomePropertyValueOrigin(profile.epc_value_origin as any),
    formatHomePropertyValueOrigin(profile.council_tax_value_origin as any),
  ].filter(Boolean)));
  const councilTaxAnnualChargeLabel = addressEnrichment?.councilTax.annualCharge != null
    ? GBP_FORMATTER.format(addressEnrichment.councilTax.annualCharge)
    : null;
  const upcomingBinCollections = addressEnrichment?.bins.entries || [];
  const hasPropertyDetails = Boolean(
    profile.council_tax_band
    || councilTaxAnnualChargeLabel
    || profile.epc_rating
    || profile.epc_score != null
    || profile.epc_expiry_date
    || profile.property_uprn
    || propertyCheckedLabel
    || profile.epc_match_status === 'ambiguous'
    || profile.epc_match_status === 'unavailable'
    || profile.council_tax_match_status === 'ambiguous'
    || profile.council_tax_match_status === 'unavailable'
  );

  return (
    <div className="space-y-3">
      <SectionAccordionCard
        emoji="🏠"
        title="Home"
        subtitle="Tenure, ownership, utilities, and the live details for where you actually live."
        open={openSection === 'profile'}
        onToggle={() => toggleHomeSection('profile')}
      >
        <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title="Saved home details"
            action={(
              <AppButton size="sm" tone="ghost" onPress={openEdit}>
                <Pencil size={12} /> Edit details
              </AppButton>
            )}
          />
        </AppCardHeader>
        <AppCardBody gap="$sm">
          {profile.address ? (
            <AppText fontSize="$md" lineHeight={22} fontWeight="600">
              {profile.address}
            </AppText>
          ) : null}
          {monthlyHomeCost ? (
            <AppText fontSize="$sm" color="$colorMuted">
              Monthly mortgage / rent: {monthlyHomeCost}
            </AppText>
          ) : null}
          {councilTaxAnnualChargeLabel ? (
            <AppText fontSize="$sm" color="$colorMuted">
              {(addressEnrichment?.councilTax.authorityName || 'Council tax')} annual charge: {councilTaxAnnualChargeLabel}
            </AppText>
          ) : null}
        </AppCardBody>
        <AppCardBody gap={0}>
          <DetailRow label="Tenure" value={profile.tenure ? TENURE_LABELS[profile.tenure] : null} />
          {renting && <DetailRow label="Deposit paid" value={profile.deposit_amount != null ? `£${profile.deposit_amount.toFixed(2)}` : null} />}
          {renting && profile.tenancy_start_date && (
            <DetailRow label="Tenancy started" value={format(parseISO(profile.tenancy_start_date), 'd MMM yyyy')} />
          )}
        </AppCardBody>
        </AppCard>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🧹"
        title="Chores & routines"
        subtitle="Recurring routines, this week's jobs, and child chore suggestions all stay together."
        open={openSection === 'jobs'}
        onToggle={() => toggleHomeSection('jobs')}
      >
        <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title="Routines and jobs"
            subtitle="Recurring home routines and this week’s jobs sit together here, with child chore suggestions only when they actually apply."
            action={(
              <AppButtonRow>
                {childChoreSuggestionGroups.length ? (
                  <AppButton size="sm" tone="outline" onPress={printChoreChart}>
                    <Printer size={12} /> Print chart
                  </AppButton>
                ) : null}
                <AppButton size="sm" onPress={() => setChoreOpen(true)}>
                  <Plus size={12} /> Add job
                </AppButton>
              </AppButtonRow>
            )}
          />
        </AppCardHeader>
        <AppCardBody className="space-y-3">
          {addressEnrichmentLoading ? (
            <AppText fontSize="$xs" color="$colorMuted">
              Checking council bin dates for the saved address…
            </AppText>
          ) : null}

          {upcomingBinCollections.length ? (
            <AppFormSection gap="$sm">
              <AppSectionHeader
                title="Bin collections"
                subtitle={addressEnrichment?.bins.weekLabel
                  ? `${addressEnrichment.bins.weekLabel} collection week from your saved address.`
                  : 'Upcoming council collections from your saved address.'}
              />
              <div className="space-y-2">
                {upcomingBinCollections.map((entry) => (
                  <AppCard key={`${entry.service}-${entry.nextCollectionDate || 'none'}`} backgroundColor="$background">
                    <AppText fontSize="$sm" fontWeight="700">{entry.service}</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">
                      {entry.nextCollectionDate ? format(parseISO(entry.nextCollectionDate), 'EEE d MMM yyyy') : 'No next date yet'}
                      {entry.scheduleLabel ? ` • ${entry.scheduleLabel}` : ''}
                    </AppText>
                    {entry.containers.length ? (
                      <AppText fontSize="$xs" color="$colorMuted">
                        {entry.containers.join(' • ')}
                      </AppText>
                    ) : null}
                  </AppCard>
                ))}
              </div>
              {addressEnrichment?.bins.calendarUrl || addressEnrichment?.bins.gardenCalendarUrl ? (
                <div className="flex flex-wrap gap-3">
                  {addressEnrichment.bins.calendarUrl ? (
                    <a
                      href={addressEnrichment.bins.calendarUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-primary underline underline-offset-2"
                    >
                      Open general calendar
                    </a>
                  ) : null}
                  {addressEnrichment.bins.gardenCalendarUrl ? (
                    <a
                      href={addressEnrichment.bins.gardenCalendarUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-primary underline underline-offset-2"
                    >
                      Open garden calendar
                    </a>
                  ) : null}
                </div>
              ) : null}
            </AppFormSection>
          ) : null}

          <Dialog
            open={choreOpen}
            onOpenChange={(open) => {
              if (open && !choreAssignee && assignees[0]) {
                setChoreAssignee(assignees[0]);
              }
              setChoreOpen(open);
            }}
          >
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Add home job</DialogTitle>
                <DialogDescription>
                  Add one shared household job and choose who it belongs with this week.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <AppFieldGroup label="Task">
                  <AppInput placeholder="What needs doing?" value={choreTask} onChange={(event) => setChoreTask(event.target.value)} />
                </AppFieldGroup>
                <AppFieldGroup label="Assign to">
                  <Select value={choreAssignee} onValueChange={setChoreAssignee}>
                    <SelectTrigger><SelectValue placeholder="Assign to..." /></SelectTrigger>
                    <SelectContent>
                      {assignees.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </AppFieldGroup>
                <AppButton onPress={handleAddChore}>Save job</AppButton>
              </div>
            </DialogContent>
          </Dialog>

          {visibleRoutineCards.length ? visibleRoutineCards.map(({ kind, routine, defaults, frequency, reminderDayOfWeek, scheduleSummary, isExpanded, title, inactiveBody, emptyAction }) => (
            <div key={kind} className="rounded-2xl border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-3">
                <AppTextActionButton
                  alignItems="flex-start"
                  aria-expanded={isExpanded}
                  flex={1}
                  minWidth={0}
                  onPress={() => toggleRoutineExpanded(kind)}
                >
                  <AppFormSection gap="$xs" flex={1}>
                    <AppText fontSize="$sm" fontWeight="700">{title}</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">
                      {scheduleSummary}
                    </AppText>
                    <AppText fontSize="$xs" color="$colorMuted">
                      {routine
                        ? routine.status === 'active'
                          ? `Next due ${format(parseISO(routine.due_date), 'd MMM yyyy')}.`
                          : 'Reminder is off for now.'
                        : `Default reminder starts ${format(parseISO(defaults.dueDate), 'd MMM yyyy')}.`}
                    </AppText>
                  </AppFormSection>
                </AppTextActionButton>
                <AppButton
                  size="sm"
                  tone={routine?.status === 'active' ? 'primary' : 'outline'}
                  onPress={() => void (routine ? setRoutineStatus(routine, routine.status === 'active' ? 'paused' : 'active') : turnOnRoutine(kind))}
                  disabled={savingRoutineKind === kind}
                >
                  {savingRoutineKind === kind ? 'Saving…' : routine?.status === 'active' ? 'On' : 'Off'}
                </AppButton>
              </div>

              {isExpanded ? (
                <AppFormSection gap="$sm" mt="$sm">
                  {routine ? (
                    <>
                      <AppText fontSize="$xs" color="$colorMuted">
                        {defaults.cadence.summary}
                      </AppText>
                      {routine.last_completed_at ? (
                        <AppText fontSize="$xs" color="$colorMuted">
                          Last completed {format(parseISO(routine.last_completed_at), 'd MMM yyyy')}
                        </AppText>
                      ) : null}
                      <AppFieldGroup label="Frequency">
                        <AppNativeSelect
                          options={SHARED_HOME_ROUTINE_FREQUENCY_OPTIONS.map((option) => ({
                            value: option.value,
                            label: option.label,
                          }))}
                          value={frequency}
                          onChange={(event) => {
                            const nextFrequency = event.target.value as SharedHomeRoutineFrequency;
                            void updateRoutineSettings(routine, { frequency: nextFrequency });
                          }}
                        />
                      </AppFieldGroup>
                      <AppFieldGroup label="Day of the week">
                        <AppNativeSelect
                          options={SHARED_HOME_BILL_WEEKDAY_OPTIONS.map((option) => ({
                            value: option,
                            label: option,
                          }))}
                          value={reminderDayOfWeek}
                          onChange={(event) => {
                            const nextReminderDay = event.target.value as SharedHomeBillWeekday;
                            void updateRoutineSettings(routine, { reminder_day_of_week: nextReminderDay });
                          }}
                        />
                      </AppFieldGroup>
                      <AppFieldGroup label="Phone notifications">
                        <AppButtonRow>
                          <AppButton
                            size="sm"
                            tone={routine.notifications_enabled ? 'primary' : 'outline'}
                            onPress={() => void updateRoutineSettings(routine, { notifications_enabled: true })}
                            disabled={savingRoutineKind === kind}
                          >
                            On
                          </AppButton>
                          <AppButton
                            size="sm"
                            tone={!routine.notifications_enabled ? 'primary' : 'outline'}
                            onPress={() => void updateRoutineSettings(routine, { notifications_enabled: false })}
                            disabled={savingRoutineKind === kind}
                          >
                            Off
                          </AppButton>
                        </AppButtonRow>
                      </AppFieldGroup>
                      <AppText fontSize="$xs" color="$colorMuted">
                        Phone reminders show in the Adulting app on your phone when notifications are allowed there.
                      </AppText>
                      <AppFieldGroup label="Device calendar sync">
                        <AppButtonRow>
                          <AppButton
                            size="sm"
                            tone={routine.calendar_sync_enabled ? 'primary' : 'outline'}
                            onPress={() => void updateRoutineSettings(routine, { calendar_sync_enabled: true })}
                            disabled={savingRoutineKind === kind}
                          >
                            On
                          </AppButton>
                          <AppButton
                            size="sm"
                            tone={!routine.calendar_sync_enabled ? 'primary' : 'outline'}
                            onPress={() => void updateRoutineSettings(routine, { calendar_sync_enabled: false })}
                            disabled={savingRoutineKind === kind}
                          >
                            Off
                          </AppButton>
                        </AppButtonRow>
                      </AppFieldGroup>
                      <AppText fontSize="$xs" color="$colorMuted">
                        {routine.calendar_sync_enabled
                          ? 'Included next time you sync Adulting Calendar to Google Calendar on web, or Apple Calendar from the iPhone app.'
                          : 'Left out of device calendar sync until you turn it on.'}
                      </AppText>
                      <AppButtonRow>
                        <AppButton
                          size="sm"
                          onPress={() => void completeRoutine(routine)}
                          disabled={savingRoutineKind === kind || routine.status === 'paused'}
                        >
                          {savingRoutineKind === kind ? 'Saving…' : 'Mark done'}
                        </AppButton>
                      </AppButtonRow>
                    </>
                  ) : (
                    <>
                      <AppText fontSize="$sm" color="$colorMuted">
                        {inactiveBody}
                      </AppText>
                      <AppText fontSize="$xs" color="$colorMuted">
                        Default reminder: {scheduleSummary}.
                      </AppText>
                      <AppButtonRow>
                        <AppButton
                          size="sm"
                          tone="outline"
                          onPress={() => void turnOnRoutine(kind)}
                          disabled={savingRoutineKind === kind}
                        >
                          {savingRoutineKind === kind ? 'Saving…' : emptyAction}
                        </AppButton>
                      </AppButtonRow>
                    </>
                  )}
                </AppFormSection>
              ) : null}
            </div>
          )) : (
            <>
              <AppText fontSize="$sm" color="$colorMuted">
                No household or garden routine reminders are turned on for this home yet.
              </AppText>
              <AppButton tone="outline" size="sm" alignSelf="flex-start" onPress={openEdit}>
                <Pencil size={12} />
                Edit details
              </AppButton>
            </>
          )}

          <div className="h-px bg-border" />

          <AppFormSection gap="$sm">
            <AppSectionHeader
              title="This week's jobs"
              subtitle="Keep the live household jobs beside the recurring routines so the practical work stays in one place."
            />
            {thisWeekChores.length ? (
              <div className="space-y-2">
                {thisWeekChores.map((chore) => (
                  <AppPanelButton
                    key={chore.id}
                    type="button"
                    onClick={() => toggleChore(chore.id)}
                    className={`gap-3 rounded-lg bg-card px-3 py-3 ${chore.done ? 'opacity-60' : ''}`}
                  >
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${chore.done ? 'border-success bg-success' : 'border-muted-foreground'}`}>
                      {chore.done ? <span className="text-primary-foreground text-xs">✓</span> : null}
                    </span>
                    <div>
                      <p className={`text-sm font-medium text-foreground ${chore.done ? 'line-through' : ''}`}>{chore.task}</p>
                      <p className="text-xs text-muted-foreground">{chore.assignedTo}</p>
                    </div>
                  </AppPanelButton>
                ))}
              </div>
            ) : (
              <AppText fontSize="$sm" color="$colorMuted">
                No home jobs are saved for this week yet.
              </AppText>
            )}
          </AppFormSection>

          {hasChildProfiles ? (
            <AppFormSection gap="$sm">
              <AppSectionHeader
                title="Suggested jobs for children"
                subtitle="Use the ages you already saved in My Settings to turn child chores into a calm starting point instead of a blank page."
              />
              {childChoreSuggestionGroups.length ? (
                <div className="space-y-3">
                  {childChoreSuggestionGroups.map((group) => (
                    <AppCard key={group.child.id} backgroundColor="$background">
                      <div>
                        <AppText fontSize="$sm" fontWeight="700">{group.child.name}</AppText>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          Age {group.child.age} · {group.ageLabel}
                        </AppText>
                      </div>
                      <div className="mt-3 space-y-2">
                        {group.suggestions.map((suggestion) => (
                          <AppCard key={`${group.child.id}-${suggestion.task}`} backgroundColor="$cardBackground" borderColor="$borderColor">
                            <div className="min-w-0">
                              <AppText fontSize="$sm" fontWeight="600">{suggestion.task}</AppText>
                              <AppText className="mt-1" fontSize="$xs" color="$colorMuted">{suggestion.note}</AppText>
                            </div>
                            {suggestion.alreadyAdded ? (
                              <span className="shrink-0 rounded-full bg-success-light px-3 py-1 text-[11px] font-medium text-success">
                                Added this week
                              </span>
                            ) : (
                              <AppButton
                                tone="outline"
                                size="sm"
                                onPress={() => addSuggestedChore(group.child.name, suggestion.task)}
                              >
                                Add
                              </AppButton>
                            )}
                          </AppCard>
                        ))}
                      </div>
                    </AppCard>
                  ))}
                </div>
              ) : (
                <AppText fontSize="$sm" color="$colorMuted">
                  Add an age to each child in My Settings and Adulting will suggest age-appropriate home jobs here.
                </AppText>
              )}
            </AppFormSection>
          ) : null}
        </AppCardBody>
        </AppCard>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🍽️"
        title="Meal planning"
        subtitle="Keep meals, shopping, and what the family is actually eating in the same Home flow."
        open={openSection === 'meals'}
        onToggle={() => toggleHomeSection('meals')}
      >
        <FamilyMealPlannerSection />
      </SectionAccordionCard>

      <BillsTab sectionOrder={['vehicle', 'bills']} forcedOpenSection={forcedBillsSection} />

      {hasPropertyDetails && (
        <AppCard mb="$sm">
          <AppCardHeader>
            <div className="flex items-center justify-between gap-3">
              <AppText fontSize="$sm" fontWeight="600" color="$colorMuted" textTransform="uppercase" letterSpacing={1.2}>
                Property details
              </AppText>
              <AppButton size="sm" tone="ghost" onPress={openEdit}>
                <Pencil size={12} /> Edit
              </AppButton>
            </div>
          </AppCardHeader>
          <AppCardBody gap={0}>
            <DetailRow label="Council tax band" value={profile.council_tax_band ? `Band ${profile.council_tax_band}` : null} />
            <DetailRow label="Council tax annual charge" value={councilTaxAnnualChargeLabel} />
            <DetailRow label="EPC rating" value={profile.epc_rating} />
            <DetailRow label="EPC score" value={profile.epc_score != null ? String(profile.epc_score) : null} />
            <DetailRow
              label="EPC expires"
              value={profile.epc_expiry_date ? format(parseISO(profile.epc_expiry_date), 'd MMM yyyy') : null}
            />
            <DetailRow label="UPRN" value={profile.property_uprn} />
          </AppCardBody>
          {(propertySourceLabels.length || propertyCheckedLabel || profile.epc_match_status === 'ambiguous' || profile.epc_match_status === 'unavailable') ? (
            <AppCardBody gap="$xs">
              {propertySourceLabels.length ? (
                <AppText fontSize="$xs" color="$colorMuted">
                  {propertySourceLabels.join(' • ')}
                </AppText>
              ) : null}
              {propertyCheckedLabel ? (
                <AppText fontSize="$xs" color="$colorMuted">
                  {propertyCheckedLabel}
                </AppText>
              ) : null}
              {addressEnrichment?.note ? (
                <AppText fontSize="$xs" color="$colorMuted">
                  {addressEnrichment.note}
                </AppText>
              ) : null}
              {profile.epc_match_status === 'ambiguous' ? (
                <AppText fontSize="$xs" color="$colorMuted">
                  Adulting found more than one likely EPC match for this address, so tighten the address or confirm the details manually.
                </AppText>
              ) : null}
              {profile.epc_match_status === 'unavailable' && !profile.epc_rating && !profile.council_tax_band ? (
                <AppText fontSize="$xs" color="$colorMuted">
                  Property lookup could not return a clear match for this address. You can still keep the EPC rating and council tax band here manually.
                </AppText>
              ) : null}
            </AppCardBody>
          ) : null}
        </AppCard>
      )}

      {owner && hasOwnerDetails && (
        <Section title="Ownership" onEdit={openEdit}>
          <DetailRow label="Mortgage provider" value={profile.mortgage_provider} />
          <DetailRow
            label="Fixed-rate ends"
            value={profile.mortgage_fixed_rate_end ? format(parseISO(profile.mortgage_fixed_rate_end), 'd MMM yyyy') : null}
          />
          <DetailRow
            label="Ground rent"
            value={profile.ground_rent_amount != null ? `£${profile.ground_rent_amount.toFixed(2)}` : null}
          />
          <DetailRow
            label="Service charge"
            value={profile.service_charge_amount != null ? `£${profile.service_charge_amount.toFixed(2)}` : null}
          />
        </Section>
      )}

      {owner && !hasOwnerDetails && (
        <AppInlineAlert title="Add the ownership details that are easiest to lose">
          Mortgage, EPC, ground rent, and service charge details belong here so remortgage and housing admin do not sprawl elsewhere.
        </AppInlineAlert>
      )}

      {owner && remortgagePrompt && (
        <AppCard mb="$sm">
          <AppCardBody>
            <AppText fontSize="$md" fontWeight="700">{remortgagePrompt.title}</AppText>
            <AppText fontSize="$sm" color="$colorMuted">
              {remortgagePrompt.body}
            </AppText>
            <AppButtonRow>
              <AppButton tone="outline" size="sm" onPress={() => navigate('/home?tab=admin')}>
                Track paperwork
              </AppButton>
              <AppButton tone="ghost" size="sm" onPress={openEdit}>
                <Pencil size={12} />
                Edit mortgage details
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>
      )}

      {/* Landlord */}
      {renting && (
        <Section title="Landlord" onEdit={openEdit}>
          <DetailRow label="Name" value={profile.landlord_name} />
          <DetailRow label="Email" value={profile.landlord_email} />
          <DetailRow label="Phone" value={profile.landlord_phone} />
        </Section>
      )}

      {/* Utilities */}
      <Section title="Utilities" onEdit={openEdit}>
        <DetailRow label="Gas" value={profile.utility_gas_provider} />
        <DetailRow
          label="Gas contract ends"
          value={profile.utility_gas_contract_end ? format(parseISO(profile.utility_gas_contract_end), 'd MMM yyyy') : null}
        />
        <DetailRow label="Electric" value={profile.utility_electric_provider} />
        <DetailRow
          label="Electric contract ends"
          value={profile.utility_electric_contract_end ? format(parseISO(profile.utility_electric_contract_end), 'd MMM yyyy') : null}
        />
        <DetailRow label="Water" value={profile.utility_water_provider} />
        <DetailRow label="Broadband" value={profile.utility_broadband_provider} />
        <DetailRow
          label="Broadband contract ends"
          value={profile.utility_broadband_contract_end ? format(parseISO(profile.utility_broadband_contract_end), 'd MMM yyyy') : null}
        />
        <DetailRow label="Phone" value={profile.utility_phone_provider} />
        <DetailRow
          label="Phone contract ends"
          value={profile.utility_phone_contract_end ? format(parseISO(profile.utility_phone_contract_end), 'd MMM yyyy') : null}
        />
      </Section>

      {utilityProvidersCount === 0 ? (
        <AppInlineAlert title="Add the utility providers you actually use">
          Once providers are here, Adulting can keep the bills view and moving-house utility admin lined up properly.
        </AppInlineAlert>
      ) : nextComparisonWindow ? (
        <AppCard mb="$sm">
          <AppCardBody>
            <AppText fontSize="$md" fontWeight="700">{nextComparisonWindow.label} is in its comparison window</AppText>
            <AppText fontSize="$sm" color="$colorMuted">
              {nextComparisonWindow.provider} ends on {format(parseISO(nextComparisonWindow.contractEnd as string), 'd MMM yyyy')}. Compare prices now before the contract rolls over and costs jump.
            </AppText>
            <AppButtonRow>
              <AppButton tone="outline" size="sm" onPress={() => navigate('/home?tab=my-home&section=bills')}>
                Review bills
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>
      ) : contractDatesMissing.length > 0 ? (
        <AppCard mb="$sm">
          <AppCardBody>
            <AppText fontSize="$md" fontWeight="700">Add contract end dates for your switchable utilities</AppText>
            <AppText fontSize="$sm" color="$colorMuted">
              {contractDatesMissing.map((item) => item.label.toLowerCase()).join(', ')} {contractDatesMissing.length === 1 ? 'still needs a date' : 'still need dates'} so Adulting can flag the best time to compare prices.
            </AppText>
            <AppButtonRow>
              <AppButton tone="outline" size="sm" onPress={openEdit}>
                <Pencil size={12} />
                Update home profile
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>
      ) : renewalUtilityProvidersCount > 0 ? (
        <AppInlineAlert title="Utility contract dates are saved">
          Adulting can now flag the comparison window before energy, broadband, or phone costs roll over.
        </AppInlineAlert>
      ) : null}

      {utilityProvidersCount === 0 && (
        <AppButtonRow mb="$sm">
          <AppButton tone="outline" size="sm" onPress={openEdit}>
            <Pencil size={12} />
            Add utility providers
          </AppButton>
        </AppButtonRow>
      )}

      {/* Other */}
      <Section title="Other" onEdit={openEdit}>
        <DetailRow label="Notes" value={profile.notes} />
      </Section>
    </div>
  );
}
